import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Lock, LogOut, CalendarDays, Plane, Repeat, Plus, Trash2,
  Check, X, ChevronLeft, ChevronRight, Pencil, Shield, Save, Users, Clock, Download, Receipt, Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { API_BASE, LOGO } from "@/content";
import { ANIME_AVATARS } from "@/avatars";

const TOKEN_KEY = "shibuya_portal_token";
const DAY_NAMES = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
const WD_SHORT = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const MONTH_NAMES = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
function mondayOf(input) {
  const date = new Date(input);
  const day = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}
function fmtDE(ymd) { const [y, m, d] = ymd.split("-"); return `${d}.${m}.${y}`; }
function fmtShort(ymd) { const [, m, d] = ymd.split("-"); return `${d}.${m}.`; }
function fmtRange(start) {
  const s = new Date(start); const e = new Date(start); e.setDate(e.getDate() + 5);
  return `${s.getDate()}.${s.getMonth() + 1}. – ${e.getDate()}.${e.getMonth() + 1}.${e.getFullYear()}`;
}
// Block 0 (09:45–11:45) only open on Saturday (weekday 5)
const blockOpen = (weekday, blockId) => !(blockId === 0 && weekday <= 4);
const firstName = (name) => (name || "").split(" ")[0];

function hslToRgb(h, s, l) {
  s /= 100; l /= 100;
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [Math.round(255 * f(0)), Math.round(255 * f(8)), Math.round(255 * f(4))];
}

export default function Portal() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [me, setMe] = useState(null);
  const [email, setEmail] = useState(() => localStorage.getItem("shibuya_portal_email") || "");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [weekStart, setWeekStart] = useState(() => toYMD(mondayOf(new Date())));
  const [showProfile, setShowProfile] = useState(false);

  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY); setToken(""); setMe(null);
  }, []);

  const handleAuthError = useCallback((err) => {
    if (err.response?.status === 401) { toast.error("Session abgelaufen – bitte neu einloggen"); logout(); return true; }
    return false;
  }, [logout]);

  const loadMe = useCallback(async () => {
    if (!token) return;
    try { const res = await axios.get(`${API_BASE}/portal/me`, auth()); setMe(res.data); }
    catch (err) { handleAuthError(err); }
  }, [token, auth, handleAuthError]);

  useEffect(() => { loadMe(); }, [loadMe]);

  const handleLogin = async (e) => {
    e.preventDefault(); setLoginError(""); setLoggingIn(true);
    try {
      const res = await axios.post(`${API_BASE}/portal/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      localStorage.setItem("shibuya_portal_email", email);
      setToken(res.data.token); setMe(res.data.employee); setPassword("");
      toast.success(`Willkommen, ${res.data.employee.name}`);
    } catch (err) { setLoginError(err.response?.data?.detail || "Login fehlgeschlagen"); }
    finally { setLoggingIn(false); }
  };

  const weekDays = useMemo(() => {
    const start = new Date(weekStart);
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(start); d.setDate(d.getDate() + i);
      return { ymd: toYMD(d), weekday: i, name: DAY_NAMES[i] };
    });
  }, [weekStart]);

  const shiftWeek = (delta) => { const d = new Date(weekStart); d.setDate(d.getDate() + delta * 7); setWeekStart(toYMD(d)); };

  if (!me) {
    return (
      <div data-testid="portal-login-page" className="hero-glow flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8">
          <img src={LOGO} alt="Shibuya Store" className="mx-auto h-12 w-auto" />
          <div className="mt-6 flex items-center justify-center gap-2">
            <Lock className="h-4 w-4 text-primary" />
            <p className="font-display text-2xl uppercase tracking-wide">Mitarbeiter-Portal</p>
          </div>
          <p className="mt-2 text-center text-sm text-muted-foreground">Schichtplan &amp; Urlaub</p>
          <form onSubmit={handleLogin} className="mt-6 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">E-Mail</Label>
              <Input type="email" name="email" autoComplete="username" data-testid="portal-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="name@shibuya-store.de" className="mt-1.5 h-11 rounded-xl border-border bg-background" autoFocus />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Passwort</Label>
              <Input type="password" name="password" autoComplete="current-password" data-testid="portal-password-input" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" className="mt-1.5 h-11 rounded-xl border-border bg-background" />
            </div>
            {loginError && <p data-testid="portal-login-error" className="text-sm text-destructive">{loginError}</p>}
            <Button type="submit" disabled={loggingIn || !email || !password} data-testid="portal-login-button"
              className="h-11 w-full rounded-xl bg-primary font-medium text-primary-foreground">
              {loggingIn ? "Wird geprüft…" : "Einloggen"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  const isAdmin = me.role === "admin";

  return (
    <div data-testid="portal-dashboard" className="mx-auto max-w-[1400px] px-3 py-6 sm:px-6 sm:py-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => setShowProfile(true)} data-testid="portal-profile-button"
            className="group relative h-11 w-11 shrink-0 overflow-hidden rounded-full border-2 border-primary/40 bg-card transition-transform hover:scale-105 sm:h-14 sm:w-14">
            {me.avatar
              ? <img src={me.avatar} alt="Avatar" className="h-full w-full object-cover" />
              : <span className="flex h-full w-full items-center justify-center font-display text-xl text-primary">{(me.name || "?")[0]}</span>}
            <span className="absolute inset-x-0 bottom-0 hidden bg-primary/80 py-0.5 text-center text-[8px] font-medium text-primary-foreground group-hover:block">Profil</span>
          </button>
          <div className="min-w-0">
            <p className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-primary sm:text-xs sm:tracking-[0.25em]">Mitarbeiter-Portal</p>
            <h1 className="mt-0.5 flex flex-wrap items-center gap-2 font-display text-2xl uppercase tracking-[0.04em] sm:mt-1 sm:text-4xl">
              {me.name}
              {isAdmin && <Badge className="rounded-full bg-primary/15 text-primary"><Shield className="mr-1 h-3 w-3" />Admin</Badge>}
            </h1>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={logout} data-testid="portal-logout-button" className="rounded-xl border-border bg-transparent">
          <LogOut className="mr-2 h-4 w-4" /> Abmelden
        </Button>
      </div>

      <ProfileDialog open={showProfile} onOpenChange={setShowProfile} me={me} setMe={setMe} auth={auth} handleAuthError={handleAuthError} />

      <div className="mt-5 flex items-center justify-between rounded-xl border border-border bg-card p-2 sm:mt-6 sm:p-3">
        <Button variant="outline" size="icon" onClick={() => shiftWeek(-1)} data-testid="portal-week-prev" className="rounded-lg border-border bg-transparent"><ChevronLeft className="h-4 w-4" /></Button>
        <div className="text-center">
          <p className="font-mono-ui text-[10px] uppercase tracking-wider text-muted-foreground sm:text-xs">Kalenderwoche</p>
          <p data-testid="portal-week-label" className="font-display text-base uppercase tracking-wide sm:text-xl">{fmtRange(weekStart)}</p>
        </div>
        <Button variant="outline" size="icon" onClick={() => shiftWeek(1)} data-testid="portal-week-next" className="rounded-lg border-border bg-transparent"><ChevronRight className="h-4 w-4" /></Button>
      </div>

      <Tabs defaultValue="schedule" className="mt-6">
        <TabsList className={`grid h-auto w-full grid-cols-2 gap-1 rounded-xl bg-card p-1 text-xs sm:text-sm ${isAdmin ? "sm:grid-cols-5" : "sm:grid-cols-4"}`}>
          <TabsTrigger value="schedule" data-testid="portal-tab-schedule" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <CalendarDays className="mr-1.5 h-4 w-4" /> Schichtplan
          </TabsTrigger>
          <TabsTrigger value="leave" data-testid="portal-tab-leave" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Plane className="mr-1.5 h-4 w-4" /> Urlaub
          </TabsTrigger>
          <TabsTrigger value="swaps" data-testid="portal-tab-swaps" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Repeat className="mr-1.5 h-4 w-4" /> Tauschbörse
          </TabsTrigger>
          <TabsTrigger value="payslips" data-testid="portal-tab-payslips" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Receipt className="mr-1.5 h-4 w-4" /> Lohn
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="employees" data-testid="portal-tab-employees" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Users className="mr-1.5 h-4 w-4" /> Mitarbeiter
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="schedule" className="mt-6">
          <ScheduleGrid {...{ auth, handleAuthError, weekStart, weekDays, isAdmin, me }} />
        </TabsContent>
        <TabsContent value="leave" className="mt-6">
          <LeaveTab {...{ auth, handleAuthError, isAdmin }} />
        </TabsContent>
        <TabsContent value="swaps" className="mt-6">
          <SwapsTab {...{ auth, handleAuthError, weekStart, weekDays, isAdmin, me }} />
        </TabsContent>
        <TabsContent value="payslips" className="mt-6">
          <PayslipsTab {...{ auth, handleAuthError, isAdmin }} />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="employees" className="mt-6">
            <EmployeesTab {...{ auth, handleAuthError }} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

/* ==================== PROFILE DIALOG (Avatar + Passwort) ==================== */
function ProfileDialog({ open, onOpenChange, me, setMe, auth, handleAuthError }) {
  const [savingAvatar, setSavingAvatar] = useState(null);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  const chooseAvatar = async (url) => {
    setSavingAvatar(url);
    try {
      const res = await axios.put(`${API_BASE}/portal/me/avatar`, { avatar: url }, auth());
      setMe(res.data);
      toast.success("Avatar aktualisiert");
    } catch (err) { if (!handleAuthError(err)) toast.error("Avatar speichern fehlgeschlagen"); }
    finally { setSavingAvatar(null); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (pw.next.length < 6) { toast.error("Neues Passwort: mindestens 6 Zeichen"); return; }
    if (pw.next !== pw.confirm) { toast.error("Passwörter stimmen nicht überein"); return; }
    setSavingPw(true);
    try {
      await axios.put(`${API_BASE}/portal/me/password`, { current_password: pw.current, new_password: pw.next }, auth());
      toast.success("Passwort geändert");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) { if (!handleAuthError(err)) toast.error(err.response?.data?.detail || "Passwort ändern fehlgeschlagen"); }
    finally { setSavingPw(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto border-border bg-card sm:max-w-2xl">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase tracking-wide">Mein Profil</DialogTitle></DialogHeader>

        <div>
          <p className="text-sm font-medium">Avatar wählen</p>
          <p className="text-xs text-muted-foreground">50 Anime-Avatare – tippe einen an.</p>
          <div data-testid="portal-avatar-grid" className="mt-3 grid grid-cols-5 gap-2 sm:grid-cols-8">
            {ANIME_AVATARS.map((url, i) => {
              const active = me.avatar === url;
              return (
                <button key={i} type="button" onClick={() => chooseAvatar(url)} disabled={savingAvatar === url}
                  data-testid={`portal-avatar-${i}`}
                  className={`relative aspect-square overflow-hidden rounded-full border-2 transition-transform hover:scale-105 ${active ? "border-primary ring-2 ring-primary" : "border-border"}`}>
                  <img src={url} alt={`Avatar ${i + 1}`} loading="lazy" className="h-full w-full object-cover" />
                  {active && <span className="absolute inset-0 flex items-center justify-center bg-primary/40"><Check className="h-4 w-4 text-white" /></span>}
                </button>
              );
            })}
          </div>
        </div>

        <form onSubmit={changePw} className="mt-2 border-t border-border pt-4">
          <p className="text-sm font-medium">Passwort ändern</p>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Input type="password" autoComplete="current-password" placeholder="Aktuelles Passwort" data-testid="portal-pw-current"
              value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="h-11 rounded-xl border-border bg-background" />
            <Input type="password" autoComplete="new-password" placeholder="Neues Passwort" data-testid="portal-pw-new"
              value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="h-11 rounded-xl border-border bg-background" />
            <Input type="password" autoComplete="new-password" placeholder="Neues wiederholen" data-testid="portal-pw-confirm"
              value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="h-11 rounded-xl border-border bg-background" />
          </div>
          <div className="mt-3 flex justify-end">
            <Button type="submit" disabled={savingPw || !pw.current || !pw.next} data-testid="portal-pw-save"
              className="h-11 rounded-xl bg-primary px-6 font-medium text-primary-foreground">
              {savingPw ? "Speichert…" : "Passwort ändern"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== SCHEDULE GRID (Excel-style) ==================== */
function ScheduleGrid({ auth, handleAuthError, weekStart, weekDays, isAdmin, me }) {
  const [blocks, setBlocks] = useState([]);
  const [cells, setCells] = useState({});
  const [employees, setEmployees] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // {date, blockId, name}
  const [monthLoading, setMonthLoading] = useState(false);
  const [activePainter, setActivePainter] = useState(null); // employee id for quick-fill mode

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/portal/grid?week_start=${weekStart}`, auth());
      setBlocks(res.data.blocks || []);
      setCells(res.data.cells || {});
      setEmployees(res.data.employees || []);
      setDirty(false);
    } catch (err) { handleAuthError(err); }
  }, [auth, handleAuthError, weekStart]);

  useEffect(() => { load(); }, [load]);

  const blockHours = useMemo(() => Object.fromEntries(blocks.map((b) => [b.id, b.hours])), [blocks]);
  const nameById = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e.name])), [employees]);

  // Stable distinct color per employee (golden-angle hue distribution)
  const hueByEmp = useMemo(() => {
    const map = {};
    employees.forEach((e, i) => { map[e.id] = Math.round((i * 137.508) % 360); });
    return map;
  }, [employees]);
  const empStyle = (id) => ({ backgroundColor: `hsl(${hueByEmp[id] ?? 0} 65% 45% / 0.22)`, color: `hsl(${hueByEmp[id] ?? 0} 85% 74%)` });
  const dotStyle = (id) => ({ backgroundColor: `hsl(${hueByEmp[id] ?? 0} 70% 55%)` });

  const hoursByEmp = useMemo(() => {
    const map = {};
    employees.forEach((e) => { map[e.id] = 0; });
    Object.entries(cells).forEach(([key, ids]) => {
      const bid = Number(key.split("|")[1]);
      const h = blockHours[bid] || 0;
      (ids || []).forEach((id) => { map[id] = (map[id] || 0) + h; });
    });
    return map;
  }, [cells, employees, blockHours]);

  const toggleEmp = (date, bid, empId) => {
    const key = `${date}|${bid}`;
    setCells((prev) => {
      const next = { ...prev };
      const arr = next[key] ? [...next[key]] : [];
      const i = arr.indexOf(empId);
      if (i >= 0) arr.splice(i, 1); else arr.push(empId);
      if (arr.length) next[key] = arr; else delete next[key];
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/portal/grid`, { week_start: weekStart, cells }, auth());
      setDirty(false);
      toast.success("Schichtplan gespeichert");
    } catch (err) { if (!handleAuthError(err)) toast.error("Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };

  const editKey = editing ? `${editing.date}|${editing.blockId}` : null;
  const editIds = editKey ? (cells[editKey] || []) : [];

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    doc.setFontSize(15);
    doc.text("Shibuya Store – Schichtplan", 14, 14);
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(`Kalenderwoche ${fmtRange(weekStart)}`, 14, 20);
    doc.setTextColor(0);

    const head = [["Tag", ...blocks.map((b) => `${b.start}\n${b.end}`)]];
    const bodyIds = [];
    const body = weekDays.map((day) => {
      const rowIds = [];
      const row = [`${day.name}\n${fmtShort(day.ymd)}`];
      blocks.forEach((b) => {
        if (!blockOpen(day.weekday, b.id)) { row.push("—"); rowIds.push([]); return; }
        const ids = cells[`${day.ymd}|${b.id}`] || [];
        rowIds.push(ids);
        row.push(ids.map((id) => firstName(nameById[id])).join("\n"));
      });
      bodyIds.push(rowIds);
      return row;
    });

    autoTable(doc, {
      head, body, startY: 24, theme: "grid",
      styles: { fontSize: 7, halign: "center", valign: "middle", cellPadding: 1.2, lineColor: [210, 210, 210] },
      headStyles: { fillColor: [25, 25, 28], textColor: 255, fontSize: 6.5 },
      columnStyles: { 0: { halign: "left", fontStyle: "bold", cellWidth: 26 } },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index > 0) {
          const ids = (bodyIds[data.row.index] || [])[data.column.index - 1] || [];
          if (ids.length === 1) { const [r, g, b] = hslToRgb(hueByEmp[ids[0]] ?? 0, 60, 80); data.cell.styles.fillColor = [r, g, b]; }
        }
      },
    });

    doc.save(`Schichtplan_${weekStart}.pdf`);
  };

  const monthRef = new Date(weekStart);
  monthRef.setDate(monthRef.getDate() + 3); // Thursday → the week's "main" month
  const monthLabel = `${MONTH_NAMES[monthRef.getMonth()]} ${monthRef.getFullYear()}`;

  const exportMonthPdf = async () => {
    setMonthLoading(true);
    try {
      const year = monthRef.getFullYear();
      const month = monthRef.getMonth();
      const first = new Date(year, month, 1);
      const last = new Date(year, month + 1, 0);
      const weekStarts = [];
      let cur = mondayOf(first);
      while (cur <= last) { weekStarts.push(toYMD(cur)); const n = new Date(cur); n.setDate(n.getDate() + 7); cur = n; }
      const results = await Promise.all(weekStarts.map((ws) => axios.get(`${API_BASE}/portal/grid?week_start=${ws}`, auth())));
      const merged = {};
      results.forEach((r) => Object.assign(merged, r.data.cells || {}));

      const daysInMonth = last.getDate();
      const body = [];
      const bodyIds = [];
      const sundayRows = [];
      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(year, month, d);
        const ymd = toYMD(date);
        const wd = (date.getDay() + 6) % 7;
        if (wd === 6) sundayRows.push(body.length);
        const rowIds = [];
        const row = [`${WD_SHORT[wd]} ${String(d).padStart(2, "0")}.${String(month + 1).padStart(2, "0")}.`];
        blocks.forEach((b) => {
          if (wd === 6 || !blockOpen(wd, b.id)) { row.push(wd === 6 ? "" : "—"); rowIds.push([]); return; }
          const ids = merged[`${ymd}|${b.id}`] || [];
          rowIds.push(ids);
          row.push(ids.map((id) => firstName(nameById[id])).join("\n"));
        });
        bodyIds.push(rowIds);
        body.push(row);
      }

      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      doc.setFontSize(15);
      doc.text(`Shibuya Store – Schichtplan ${MONTH_NAMES[month]} ${year}`, 14, 14);
      autoTable(doc, {
        head: [["Tag", ...blocks.map((b) => `${b.start}\n${b.end}`)]],
        body, startY: 20, theme: "grid",
        styles: { fontSize: 6.2, halign: "center", valign: "middle", cellPadding: 0.8, lineColor: [210, 210, 210] },
        headStyles: { fillColor: [25, 25, 28], textColor: 255, fontSize: 6 },
        columnStyles: { 0: { halign: "left", fontStyle: "bold", cellWidth: 24 } },
        didParseCell: (data) => {
          if (data.section !== "body") return;
          if (sundayRows.includes(data.row.index)) { data.cell.styles.fillColor = [238, 238, 238]; return; }
          if (data.column.index > 0) {
            const ids = (bodyIds[data.row.index] || [])[data.column.index - 1] || [];
            if (ids.length === 1) { const [r, g, b] = hslToRgb(hueByEmp[ids[0]] ?? 0, 60, 80); data.cell.styles.fillColor = [r, g, b]; }
          }
        },
      });

      doc.save(`Schichtplan_${year}-${String(month + 1).padStart(2, "0")}.pdf`);
    } catch (err) { if (!handleAuthError(err)) toast.error("Monats-PDF fehlgeschlagen"); }
    finally { setMonthLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {isAdmin ? "Tippe auf eine Zelle, um Mitarbeiter zuzuweisen. Stunden werden automatisch berechnet." : "Dein Wochenplan. Deine Schichten sind hervorgehoben."}
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportPdf} disabled={!blocks.length} data-testid="portal-grid-pdf"
            className="h-10 rounded-xl border-border bg-transparent px-4 font-medium">
            <Download className="mr-2 h-4 w-4" /> Woche
          </Button>
          <Button variant="outline" onClick={exportMonthPdf} disabled={!blocks.length || monthLoading} data-testid="portal-grid-pdf-month"
            className="h-10 rounded-xl border-border bg-transparent px-4 font-medium">
            <Download className="mr-2 h-4 w-4" /> {monthLoading ? "Erstellt…" : monthLabel}
          </Button>
          {isAdmin && (
            <Button onClick={save} disabled={saving || !dirty} data-testid="portal-grid-save"
              className="h-10 rounded-xl bg-primary px-5 font-medium text-primary-foreground">
              <Save className="mr-2 h-4 w-4" /> {saving ? "Speichert…" : dirty ? "Plan speichern" : "Gespeichert"}
            </Button>
          )}
        </div>
      </div>

      {isAdmin && (
        <div data-testid="portal-painter-bar" className="rounded-2xl border border-border bg-card p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Schnell-Modus: Mitarbeiter wählen, dann Zellen antippen zum Befüllen
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {employees.map((e) => {
              const on = activePainter === e.id;
              return (
                <button key={e.id} type="button" data-testid={`portal-painter-${e.id}`}
                  onClick={() => setActivePainter(on ? null : e.id)}
                  style={on ? empStyle(e.id) : undefined}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    on ? "border-primary" : "border-border bg-background hover:border-primary/50"
                  }`}>
                  <span className="inline-block h-2.5 w-2.5 rounded-full" style={dotStyle(e.id)} />
                  {firstName(e.name)}
                </button>
              );
            })}
            {activePainter && (
              <Button size="sm" variant="ghost" onClick={() => setActivePainter(null)} data-testid="portal-painter-clear"
                className="h-7 rounded-full px-3 text-xs text-muted-foreground">
                <X className="mr-1 h-3 w-3" /> Modus beenden
              </Button>
            )}
          </div>
          {activePainter && (
            <p className="mt-2 text-xs text-primary">
              Aktiv: <strong>{nameById[activePainter]}</strong> – tippe Zellen an (nochmal tippen entfernt). Für Mehrfachauswahl pro Zelle „Modus beenden" und Zelle direkt anklicken.
            </p>
          )}
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-border bg-card">
        <p className="px-3 pt-2 text-[10px] text-muted-foreground sm:hidden">← Tabelle horizontal wischen →</p>
        <table data-testid="portal-grid-table" className="w-full border-collapse text-xs">
          <thead>
            <tr className="border-b border-border">
              <th className="sticky left-0 z-10 bg-card px-2 py-2 text-left font-mono-ui uppercase tracking-wider text-muted-foreground">Tag</th>
              {blocks.map((b) => (
                <th key={b.id} className="min-w-[78px] border-l border-border px-1 py-2 text-center font-mono-ui text-[10px] text-muted-foreground">
                  <div>{b.start}</div><div className="text-primary/70">{b.end}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weekDays.map((day) => (
              <tr key={day.ymd} className="border-b border-border last:border-0">
                <td className="sticky left-0 z-10 bg-card px-2 py-2 align-top">
                  <div className="font-display text-sm uppercase leading-tight">{day.name}</div>
                  <div className="font-mono-ui text-[10px] text-muted-foreground">{fmtShort(day.ymd)}</div>
                </td>
                {blocks.map((b) => {
                  const open = blockOpen(day.weekday, b.id);
                  const key = `${day.ymd}|${b.id}`;
                  const ids = cells[key] || [];
                  if (!open) {
                    return <td key={b.id} className="border-l border-border bg-muted/20 text-center text-muted-foreground/40">·</td>;
                  }
                  return (
                    <td key={b.id} data-testid={`portal-cell-${day.ymd}-${b.id}`}
                      onClick={isAdmin ? () => (activePainter ? toggleEmp(day.ymd, b.id, activePainter) : setEditing({ date: day.ymd, blockId: b.id, name: day.name })) : undefined}
                      className={`border-l border-border p-1 align-top ${isAdmin ? "cursor-pointer hover:bg-primary/10" : ""}`}>
                      <div className="flex flex-col gap-0.5">
                        {ids.map((id) => (
                          <span key={id} style={empStyle(id)}
                            className={`truncate rounded px-1 py-0.5 text-[10px] font-medium ${id === me.id ? "ring-1 ring-inset ring-primary" : ""}`}>
                            {firstName(nameById[id]) || "?"}
                          </span>
                        ))}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Hours summary (admin only) */}
      {isAdmin && (
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary" />
          <p className="font-display text-lg uppercase tracking-wide">Stunden pro Mitarbeiter (Woche)</p>
        </div>
        <div data-testid="portal-hours-summary" className="mt-3 flex flex-wrap gap-2">
          {employees.map((e) => {
            const h = hoursByEmp[e.id] || 0;
            const over = h > e.max_hours_per_week + 1e-6;
            return (
              <Badge key={e.id} variant="secondary"
                className={`rounded-full ${over ? "bg-destructive/20 text-destructive" : ""}`}>
                <span className="mr-1.5 inline-block h-2 w-2 rounded-full" style={dotStyle(e.id)} />
                {e.name}: {h}h / {e.max_hours_per_week}h
              </Badge>
            );
          })}
        </div>
      </div>
      )}

      {/* Cell editor dialog (admin) */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="border-border bg-card">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-wide">
              {editing && `${editing.name}, ${blocks.find((b) => b.id === editing.blockId)?.start}–${blocks.find((b) => b.id === editing.blockId)?.end}`}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Mitarbeiter für diese Zelle wählen (mehrere möglich):</p>
          <div className="mt-1 max-h-[50vh] space-y-1.5 overflow-y-auto">
            {employees.map((e) => {
              const on = editIds.includes(e.id);
              const h = hoursByEmp[e.id] || 0;
              return (
                <button key={e.id} type="button" data-testid={`portal-cell-emp-${e.id}`}
                  onClick={() => editing && toggleEmp(editing.date, editing.blockId, e.id)}
                  className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left transition-colors ${
                    on ? "border-primary bg-primary/15" : "border-border bg-background hover:border-primary/50"
                  }`}>
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <span className="inline-block h-2.5 w-2.5 rounded-full" style={dotStyle(e.id)} />
                    {e.name}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className={`font-mono-ui text-xs ${h > e.max_hours_per_week + 1e-6 ? "text-destructive" : "text-muted-foreground"}`}>{h}h / {e.max_hours_per_week}h</span>
                    {on && <Check className="h-4 w-4 text-primary" />}
                  </span>
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button onClick={() => setEditing(null)} className="rounded-xl bg-primary font-medium text-primary-foreground">Fertig</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ==================== LEAVE TAB ==================== */
function LeaveTab({ auth, handleAuthError, isAdmin }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ start_date: "", end_date: "", reason: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const url = isAdmin ? `${API_BASE}/portal/leave` : `${API_BASE}/portal/leave/mine`;
      const res = await axios.get(url, auth()); setItems(res.data || []);
    } catch (err) { handleAuthError(err); }
  }, [auth, handleAuthError, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date) { toast.error("Bitte Start- und Enddatum wählen"); return; }
    setSaving(true);
    try { await axios.post(`${API_BASE}/portal/leave`, form, auth()); toast.success("Urlaubsantrag eingereicht"); setForm({ start_date: "", end_date: "", reason: "" }); load(); }
    catch (err) { if (!handleAuthError(err)) toast.error(err.response?.data?.detail || "Antrag fehlgeschlagen"); }
    finally { setSaving(false); }
  };
  const decide = async (id, status) => {
    try { await axios.put(`${API_BASE}/portal/leave/${id}`, { status }, auth()); toast.success(status === "approved" ? "Genehmigt" : "Abgelehnt"); load(); }
    catch (err) { if (!handleAuthError(err)) toast.error("Aktion fehlgeschlagen"); }
  };
  const remove = async (id) => {
    try { await axios.delete(`${API_BASE}/portal/leave/${id}`, auth()); load(); }
    catch (err) { if (!handleAuthError(err)) toast.error("Löschen fehlgeschlagen"); }
  };
  const statusBadge = (s) => s === "approved"
    ? <Badge className="rounded-full bg-emerald-500/20 text-emerald-400">Genehmigt</Badge>
    : s === "rejected" ? <Badge variant="destructive" className="rounded-full">Abgelehnt</Badge>
    : <Badge variant="secondary" className="rounded-full">Offen</Badge>;

  return (
    <div className="space-y-6">
      {!isAdmin && (
        <form onSubmit={submit} data-testid="portal-leave-form" className="rounded-2xl border border-border bg-card p-6">
          <p className="font-display text-xl uppercase tracking-wide">Urlaub beantragen</p>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Von</Label>
              <Input type="date" data-testid="portal-leave-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5 h-11 rounded-xl border-border bg-background" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Bis</Label>
              <Input type="date" data-testid="portal-leave-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1.5 h-11 rounded-xl border-border bg-background" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Grund (optional)</Label>
              <Textarea data-testid="portal-leave-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5 min-h-[70px] rounded-xl border-border bg-background" />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button type="submit" disabled={saving} data-testid="portal-leave-submit" className="h-11 rounded-xl bg-primary px-6 font-medium text-primary-foreground">
              <Plus className="mr-1.5 h-4 w-4" /> {saving ? "Reicht ein…" : "Antrag einreichen"}
            </Button>
          </div>
        </form>
      )}
      <div>
        <p className="font-display text-xl uppercase tracking-wide">
          {isAdmin ? "Alle Urlaubsanträge" : "Meine Anträge"} <span className="text-muted-foreground">({items.length})</span>
        </p>
        <div data-testid="portal-leave-list" className="mt-4 space-y-3">
          {items.length === 0 ? (
            <p className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">Keine Anträge.</p>
          ) : items.map((lv) => (
            <div key={lv.id} data-testid={`portal-leave-row-${lv.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  {isAdmin && <span className="font-medium">{lv.employee_name}</span>}
                  {statusBadge(lv.status)}
                </div>
                <p className="mt-1 text-sm">{fmtDE(lv.start_date)} – {fmtDE(lv.end_date)}</p>
                {lv.reason && <p className="text-xs text-muted-foreground">{lv.reason}</p>}
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && lv.status === "pending" && (
                  <>
                    <Button size="sm" onClick={() => decide(lv.id, "approved")} data-testid={`portal-leave-approve-${lv.id}`} className="rounded-lg bg-emerald-600 hover:bg-emerald-700"><Check className="h-4 w-4" /></Button>
                    <Button size="sm" variant="destructive" onClick={() => decide(lv.id, "rejected")} data-testid={`portal-leave-reject-${lv.id}`} className="rounded-lg"><X className="h-4 w-4" /></Button>
                  </>
                )}
                {(isAdmin || lv.status === "pending") && (
                  <Button size="icon" variant="outline" onClick={() => remove(lv.id)} data-testid={`portal-leave-delete-${lv.id}`} className="rounded-lg border-border bg-transparent"><Trash2 className="h-4 w-4" /></Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ==================== SWAPS TAB (day-level) ==================== */
function SwapsTab({ auth, handleAuthError, weekStart, weekDays, isAdmin, me }) {
  const [offers, setOffers] = useState([]);
  const [myDays, setMyDays] = useState([]); // [{ymd, name, hours, label}]
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const sw = await axios.get(`${API_BASE}/portal/swaps?week_start=${weekStart}`, auth());
      setOffers(sw.data || []);
      const grid = await axios.get(`${API_BASE}/portal/grid?week_start=${weekStart}`, auth());
      const cells = grid.data.cells || {};
      const blockHours = Object.fromEntries((grid.data.blocks || []).map((b) => [b.id, b.hours]));
      const blockById = Object.fromEntries((grid.data.blocks || []).map((b) => [b.id, b]));
      const days = weekDays.map((d) => {
        const bids = [];
        Object.entries(cells).forEach(([key, ids]) => {
          const [date, bid] = key.split("|");
          if (date === d.ymd && (ids || []).includes(me.id)) bids.push(Number(bid));
        });
        if (!bids.length) return null;
        bids.sort((a, b) => a - b);
        const hours = bids.reduce((a, b) => a + (blockHours[b] || 0), 0);
        const label = `${blockById[bids[0]]?.start}–${blockById[bids[bids.length - 1]]?.end}`;
        return { ymd: d.ymd, name: d.name, hours: Math.round(hours * 100) / 100, label };
      }).filter(Boolean);
      setMyDays(days);
    } catch (err) { handleAuthError(err); }
  }, [auth, handleAuthError, weekStart, weekDays, me.id]);

  useEffect(() => { load(); }, [load]);

  const offer = async (date) => {
    try { await axios.post(`${API_BASE}/portal/swaps`, { date }, auth()); toast.success("Tag zum Tausch angeboten"); setOpen(false); load(); }
    catch (err) { if (!handleAuthError(err)) toast.error(err.response?.data?.detail || "Fehlgeschlagen"); }
  };
  const accept = async (id) => {
    try { await axios.post(`${API_BASE}/portal/swaps/${id}/accept`, {}, auth()); toast.success("Übernahme angefragt – wartet auf Freigabe"); load(); }
    catch (err) { if (!handleAuthError(err)) toast.error(err.response?.data?.detail || "Fehlgeschlagen"); }
  };
  const decide = async (id, status) => {
    try { await axios.put(`${API_BASE}/portal/swaps/${id}/decide`, { status }, auth()); toast.success(status === "approved" ? "Tausch freigegeben" : "Abgelehnt"); load(); }
    catch (err) { if (!handleAuthError(err)) toast.error("Fehlgeschlagen"); }
  };
  const remove = async (id) => {
    try { await axios.delete(`${API_BASE}/portal/swaps/${id}`, auth()); load(); }
    catch (err) { if (!handleAuthError(err)) toast.error("Fehlgeschlagen"); }
  };

  const swapBadge = (s) => s === "open" ? <Badge className="rounded-full bg-amber-500/20 text-amber-400">Offen</Badge>
    : s === "accepted" ? <Badge className="rounded-full bg-blue-500/20 text-blue-400">Angenommen – wartet auf Freigabe</Badge>
    : s === "approved" ? <Badge className="rounded-full bg-emerald-500/20 text-emerald-400">Getauscht</Badge>
    : <Badge variant="secondary" className="rounded-full">{s}</Badge>;

  const offeredDates = new Set(offers.filter((o) => ["open", "accepted"].includes(o.status) && o.from_employee_id === me.id).map((o) => o.date));

  return (
    <div className="space-y-6">
      {!isAdmin && (
        <div className="flex items-center justify-between rounded-2xl border border-border bg-card p-5">
          <div>
            <p className="font-display text-xl uppercase tracking-wide">Tag abgeben</p>
            <p className="mt-1 text-sm text-muted-foreground">Biete einen deiner verplanten Tage zum Tausch an.</p>
          </div>
          <Button onClick={() => setOpen(true)} data-testid="portal-swap-offer-button" className="h-11 rounded-xl bg-primary px-6 font-medium text-primary-foreground">
            <Repeat className="mr-2 h-4 w-4" /> Tag anbieten
          </Button>
        </div>
      )}

      <div>
        <p className="font-display text-xl uppercase tracking-wide">Tauschangebote <span className="text-muted-foreground">({offers.length})</span></p>
        <div data-testid="portal-swaps-list" className="mt-4 space-y-3">
          {offers.length === 0 ? (
            <p className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">Keine offenen Tauschangebote diese Woche.</p>
          ) : offers.map((o) => (
            <div key={o.id} data-testid={`portal-swap-row-${o.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
              <div>
                <div className="flex flex-wrap items-center gap-2">{swapBadge(o.status)}</div>
                <p className="mt-1 text-sm">{[fmtDE(o.date), o.time_label, o.hours != null ? `${o.hours}h` : null].filter(Boolean).join(" · ")}</p>
                <p className="text-xs text-muted-foreground">Von: {o.from_employee_name}{o.accepted_by_name ? ` → ${o.accepted_by_name}` : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                {!isAdmin && o.status === "open" && o.from_employee_id !== me.id && (
                  <Button size="sm" onClick={() => accept(o.id)} data-testid={`portal-swap-accept-${o.id}`} className="rounded-lg bg-primary text-primary-foreground">Übernehmen</Button>
                )}
                {isAdmin && o.status === "accepted" && (
                  <>
                    <Button size="sm" onClick={() => decide(o.id, "approved")} data-testid={`portal-swap-approve-${o.id}`} className="rounded-lg bg-emerald-600 hover:bg-emerald-700"><Check className="h-4 w-4" /></Button>
                    <Button size="sm" variant="destructive" onClick={() => decide(o.id, "rejected")} data-testid={`portal-swap-reject-${o.id}`} className="rounded-lg"><X className="h-4 w-4" /></Button>
                  </>
                )}
                {(isAdmin || o.from_employee_id === me.id) && o.status !== "approved" && (
                  <Button size="icon" variant="outline" onClick={() => remove(o.id)} data-testid={`portal-swap-delete-${o.id}`} className="rounded-lg border-border bg-transparent"><Trash2 className="h-4 w-4" /></Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-border bg-card">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase tracking-wide">Tag anbieten</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {myDays.length === 0 ? (
              <p className="text-sm text-muted-foreground">Du hast diese Woche keine verplanten Tage.</p>
            ) : myDays.map((d) => (
              <button key={d.ymd} type="button" disabled={offeredDates.has(d.ymd)} onClick={() => offer(d.ymd)} data-testid={`portal-swap-myday-${d.ymd}`}
                className="flex w-full items-center justify-between rounded-lg border border-border bg-background px-3 py-2 text-left hover:border-primary/50 disabled:opacity-40">
                <span className="text-sm">{d.name}, {fmtDE(d.ymd)} · {d.label} · {d.hours}h</span>
                {offeredDates.has(d.ymd) ? <span className="text-xs text-muted-foreground">bereits angeboten</span> : <Repeat className="h-4 w-4 text-primary" />}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ==================== EMPLOYEES TAB (admin) ==================== */
function EmployeesTab({ auth, handleAuthError }) {
  const empty = { name: "", email: "", password: "", role: "employee", max_hours_per_week: 40, active: true };
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const res = await axios.get(`${API_BASE}/portal/employees`, auth()); setList(res.data || []); }
    catch (err) { handleAuthError(err); }
  }, [auth, handleAuthError]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.error("Name, E-Mail und Passwort sind Pflicht"); return; }
    setSaving(true);
    try { await axios.post(`${API_BASE}/portal/employees`, { ...form, max_hours_per_week: Number(form.max_hours_per_week) }, auth()); toast.success("Mitarbeiter angelegt"); setForm(empty); load(); }
    catch (err) { if (!handleAuthError(err)) toast.error(err.response?.data?.detail || "Fehlgeschlagen"); }
    finally { setSaving(false); }
  };
  const update = async () => {
    if (!edit) return; setSaving(true);
    try {
      const payload = { name: edit.name, email: edit.email, role: edit.role, max_hours_per_week: Number(edit.max_hours_per_week), active: edit.active };
      if (edit.password) payload.password = edit.password;
      await axios.put(`${API_BASE}/portal/employees/${edit.id}`, payload, auth()); toast.success("Gespeichert"); setEdit(null); load();
    } catch (err) { if (!handleAuthError(err)) toast.error(err.response?.data?.detail || "Fehlgeschlagen"); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    try { await axios.delete(`${API_BASE}/portal/employees/${id}`, auth()); toast.success("Gelöscht"); load(); }
    catch (err) { if (!handleAuthError(err)) toast.error("Löschen fehlgeschlagen"); }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={create} data-testid="portal-employee-form" className="rounded-2xl border border-border bg-card p-6">
        <p className="font-display text-xl uppercase tracking-wide">Neuer Mitarbeiter</p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
            <Input data-testid="portal-emp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 rounded-xl border-border bg-background" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">E-Mail</Label>
            <Input type="email" data-testid="portal-emp-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 rounded-xl border-border bg-background" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Passwort</Label>
            <Input type="text" data-testid="portal-emp-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min. 6 Zeichen" className="mt-1.5 h-11 rounded-xl border-border bg-background" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Max. Stunden / Woche</Label>
            <Input type="number" min="0" step="0.5" data-testid="portal-emp-hours" value={form.max_hours_per_week} onChange={(e) => setForm({ ...form, max_hours_per_week: e.target.value })} className="mt-1.5 h-11 rounded-xl border-border bg-background" />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={form.role === "admin"} onCheckedChange={(v) => setForm({ ...form, role: v ? "admin" : "employee" })} data-testid="portal-emp-admin-switch" />
            Admin-Rechte
          </label>
          <Button type="submit" disabled={saving} data-testid="portal-emp-create" className="h-11 rounded-xl bg-primary px-6 font-medium text-primary-foreground">
            <Plus className="mr-1.5 h-4 w-4" /> {saving ? "Speichert…" : "Anlegen"}
          </Button>
        </div>
      </form>

      <div>
        <p className="font-display text-xl uppercase tracking-wide">Team <span className="text-muted-foreground">({list.length})</span></p>
        <div data-testid="portal-employees-list" className="mt-4 space-y-3">
          {list.map((e) => (
            <div key={e.id} data-testid={`portal-emp-row-${e.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{e.name}</span>
                  {e.role === "admin" && <Badge className="rounded-full bg-primary/15 text-primary">Admin</Badge>}
                  {!e.active && <Badge variant="secondary" className="rounded-full">Inaktiv</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">{e.email} · max. {e.max_hours_per_week}h/Woche</p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="icon" variant="outline" onClick={() => setEdit({ ...e, password: "" })} data-testid={`portal-emp-edit-${e.id}`} className="rounded-lg border-border bg-transparent"><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="outline" onClick={() => remove(e.id)} data-testid={`portal-emp-delete-${e.id}`} className="rounded-lg border-border bg-transparent"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="border-border bg-card">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase tracking-wide">Mitarbeiter bearbeiten</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
                <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="portal-edit-name" className="mt-1.5 h-11 rounded-xl border-border bg-background" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">E-Mail</Label>
                <Input value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} data-testid="portal-edit-email" className="mt-1.5 h-11 rounded-xl border-border bg-background" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Neues Passwort (optional)</Label>
                <Input type="text" value={edit.password} onChange={(e) => setEdit({ ...edit, password: e.target.value })} data-testid="portal-edit-password" placeholder="leer = unverändert" className="mt-1.5 h-11 rounded-xl border-border bg-background" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Max. Stunden / Woche</Label>
                <Input type="number" min="0" step="0.5" value={edit.max_hours_per_week} onChange={(e) => setEdit({ ...edit, max_hours_per_week: e.target.value })} data-testid="portal-edit-hours" className="mt-1.5 h-11 rounded-xl border-border bg-background" />
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <Switch checked={edit.role === "admin"} onCheckedChange={(v) => setEdit({ ...edit, role: v ? "admin" : "employee" })} /> Admin-Rechte
              </label>
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <Switch checked={edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: v })} /> Aktiv
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)} className="rounded-xl border-border bg-transparent">Abbrechen</Button>
            <Button onClick={update} disabled={saving} data-testid="portal-edit-save" className="rounded-xl bg-primary font-medium text-primary-foreground">{saving ? "Speichert…" : "Speichern"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ==================== PAYSLIPS TAB (Lohnabrechnungen) ==================== */
function PayslipsTab({ auth, handleAuthError, isAdmin }) {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ employee_id: "", label: "", file: null });
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const url = isAdmin ? `${API_BASE}/portal/payslips` : `${API_BASE}/portal/payslips/mine`;
      const res = await axios.get(url, auth());
      setItems(res.data || []);
      if (isAdmin) {
        const emps = await axios.get(`${API_BASE}/portal/employees`, auth());
        setEmployees(emps.data || []);
      }
    } catch (err) { handleAuthError(err); }
  }, [auth, handleAuthError, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!form.employee_id || !form.label.trim() || !form.file) { toast.error("Mitarbeiter, Bezeichnung und Datei sind Pflicht"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("employee_id", form.employee_id);
      fd.append("label", form.label.trim());
      fd.append("file", form.file);
      await axios.post(`${API_BASE}/portal/payslips`, fd, auth());
      toast.success("Lohnabrechnung hochgeladen");
      setForm({ employee_id: "", label: "", file: null });
      document.getElementById("payslip-file-input").value = "";
      load();
    } catch (err) { if (!handleAuthError(err)) toast.error(err.response?.data?.detail || "Upload fehlgeschlagen"); }
    finally { setUploading(false); }
  };

  const download = async (p) => {
    try {
      const res = await axios.get(`${API_BASE}/portal/payslips/${p.id}/download`, { ...auth(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = p.filename || `${p.label}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { if (!handleAuthError(err)) toast.error("Download fehlgeschlagen"); }
  };

  const remove = async (id) => {
    try { await axios.delete(`${API_BASE}/portal/payslips/${id}`, auth()); toast.success("Gelöscht"); load(); }
    catch (err) { if (!handleAuthError(err)) toast.error("Löschen fehlgeschlagen"); }
  };

  const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

  return (
    <div className="space-y-6">
      {isAdmin && (
        <form onSubmit={upload} data-testid="portal-payslip-form" className="rounded-2xl border border-border bg-card p-6">
          <p className="font-display text-xl uppercase tracking-wide">Lohnabrechnung hochladen</p>
          <p className="mt-1 text-sm text-muted-foreground">Die Datei ist nur für den gewählten Mitarbeiter sichtbar.</p>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Mitarbeiter</Label>
              <select data-testid="portal-payslip-employee" value={form.employee_id}
                onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                className="mt-1.5 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm">
                <option value="">– wählen –</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Bezeichnung</Label>
              <Input data-testid="portal-payslip-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="z. B. Lohnabrechnung Juni 2026" className="mt-1.5 h-11 rounded-xl border-border bg-background" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Datei (PDF)</Label>
              <Input id="payslip-file-input" type="file" accept="application/pdf,image/*" data-testid="portal-payslip-file"
                onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                className="mt-1.5 h-11 rounded-xl border-border bg-background file:mr-3 file:rounded file:border-0 file:bg-primary/20 file:px-3 file:py-1 file:text-primary" />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button type="submit" disabled={uploading} data-testid="portal-payslip-upload" className="h-11 rounded-xl bg-primary px-6 font-medium text-primary-foreground">
              <Upload className="mr-1.5 h-4 w-4" /> {uploading ? "Lädt hoch…" : "Hochladen"}
            </Button>
          </div>
        </form>
      )}

      <div>
        <p className="font-display text-xl uppercase tracking-wide">
          {isAdmin ? "Alle Lohnabrechnungen" : "Meine Lohnabrechnungen"} <span className="text-muted-foreground">({items.length})</span>
        </p>
        <div data-testid="portal-payslips-list" className="mt-4 space-y-3">
          {items.length === 0 ? (
            <p className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
              {isAdmin ? "Noch keine Lohnabrechnungen hochgeladen." : "Es liegen noch keine Lohnabrechnungen für dich bereit."}
            </p>
          ) : items.map((p) => (
            <div key={p.id} data-testid={`portal-payslip-row-${p.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
              <div className="flex items-center gap-3">
                <Receipt className="h-5 w-5 text-primary" />
                <div>
                  <p className="font-medium">{p.label}</p>
                  <p className="text-xs text-muted-foreground">
                    {isAdmin ? `${p.employee_name} · ` : ""}{p.filename} · {fmtSize(p.size)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => download(p)} data-testid={`portal-payslip-download-${p.id}`} className="rounded-lg bg-primary text-primary-foreground">
                  <Download className="mr-1.5 h-4 w-4" /> Download
                </Button>
                {isAdmin && (
                  <Button size="icon" variant="outline" onClick={() => remove(p.id)} data-testid={`portal-payslip-delete-${p.id}`} className="rounded-lg border-border bg-transparent">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
